import { useCallback, useState } from 'react'
import DevSection from './DevSection'
import FootballStatusPanel from './FootballStatusPanel'
import { useDevPoll } from './useDevPoll'
import { confirmResetEverything, confirmResync } from './confirmations'
import { summariseDeleted, summariseSync } from './resyncReadout'
import { getFootballStatus, resetEverything, resyncFootballData } from '../../../api/dev'
import { isDevToolAvailable } from '../../../devToolCapabilities'
import type { DataMode } from '../../../dataMode'
import type { FootballStatusResponse } from '../../../footballStatus'

/** Cheap — it reads our own tables and spends no provider requests. */
export const STATUS_POLL_MS = 10_000

/**
 * What the pipeline is doing, and the controls that can make it do it again.
 *
 * Available in every mode, deliberately: in `live` this section is the entire reason
 * stopped hiding the rail, and `resync` is the recovery path when a mapping bug or a partial sync
 * left bad fixtures — refusing it there would mean the only fix is a server restart against a
 * hand-edited database.
 */

export default function PipelineSection({
  mode,
  open,
}: {
  mode: DataMode | null
  open: boolean
}) {
  const showStatus = isDevToolAvailable('footballStatus', mode)
  const showResync = isDevToolAvailable('resyncFootballData', mode)
  const showResetEverything = isDevToolAvailable('resetEverything', mode)
  const [resyncNote, setResyncNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => getFootballStatus(), [])
  const { data, error, receivedAtMs, refresh } = useDevPoll<FootballStatusResponse>(load, {
    enabled: open && showStatus,
    intervalMs: STATUS_POLL_MS,
  })

  if (!showStatus && !showResync && !showResetEverything) return null

  const cost = {
    // The estimate rides on the status payload, so the confirmation can state the cost *and*
    // today's usage — "about 3 requests" means something different at 12/2000 than at 1990/2000.
    estimatedRequests: data?.resyncEstimates.all ?? 3,
    requestsToday: data?.requestsToday ?? 0,
    dailyBudget: data?.dailyBudget ?? 0,
  }

  const runResync = async () => {
    const confirmed = confirmResync(mode, cost)
    if (!confirmed.ok) return
    setBusy(true)
    setResyncNote('resyncing…')
    try {
      const outcome = await resyncFootballData('all', confirmed.confirm)
      setResyncNote(
        `deleted ${summariseDeleted(outcome.deleted)} — re-fetched ${summariseSync(outcome.synced)}`
      )
      refresh()
      // `refresh()` refreshes this panel's own poll and nothing else — no fixture,
      // result, standings or league query is invalidated, so even a resync that worked perfectly
      // showed the pre-resync screen until the user reloaded by hand. A reload is the honest fix
      // for a control pressed once in a while; the clock controls, pressed repeatedly, get the
      // cheaper path instead.
      reloadSoon()
    } catch (failure) {
      setResyncNote(failure instanceof Error ? failure.message : String(failure))
    } finally {
      setBusy(false)
    }
  }

  const runResetEverything = async () => {
    const confirmed = confirmResetEverything(mode, cost)
    if (!confirmed.ok) return
    setBusy(true)
    setResyncNote('resetting everything…')
    try {
      const outcome = await resetEverything(confirmed.confirm)
      const anchor = outcome.replica.reanchored
        ? `season re-anchored to ${new Date(outcome.replica.seasonStartMs ?? 0).toISOString()}`
        : `replica clock unchanged: ${outcome.replica.reason ?? 'not replica mode'}`
      setResyncNote(
        `wiped ${summariseDeleted(outcome.deleted)} — ${anchor} — ` +
          `re-synced ${summariseSync(outcome.synced)}`
      )
      refresh()
      reloadSoon()
    } catch (failure) {
      setResyncNote(failure instanceof Error ? failure.message : String(failure))
    } finally {
      setBusy(false)
    }
  }

  return (
    <DevSection title="Pipeline">
      {showStatus && <FootballStatusPanel status={data} error={error} nowMs={receivedAtMs} />}
      {showResync && (
        <button
          className="gameweek-grid__dev-btn"
          disabled={busy}
          onClick={() => void runResync()}
          title="Dev: delete and re-fetch fixtures, standings and match detail (costs provider requests)"
        >
          <span className="gameweek-grid__dev-btn-label">Resync football</span>♻️
        </button>
      )}
      {showResetEverything && (
        <button
          className="gameweek-grid__dev-btn"
          disabled={busy}
          onClick={() => void runResetEverything()}
          title="Dev: wipe gameplay AND football data, re-anchor the replica season, and re-sync"
        >
          <span className="gameweek-grid__dev-btn-label">Reset everything</span>💣
        </button>
      )}
      {resyncNote !== null && <div className="gameweek-grid__dev-readout">{resyncNote}</div>}
    </DevSection>
  )
}

/**
 * Long enough for the readout to be legible before the page goes.
 *
 * The readout is the only record of what the operation did — nothing persists it — so reloading the
 * instant the request returns would throw away the answer to the question that was just asked.
 */
export const RELOAD_DELAY_MS = 1_500

function reloadSoon() {
  setTimeout(() => window.location.reload(), RELOAD_DELAY_MS) // dev
}
